import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { customerPortalApi, CustomerPortalSummary } from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";
import { customerPortalSummaryErrorMessage } from "@/lib/customerPortalSummaryError";

/** "year": pedidos feitos no ano civil; "cycle": pedidos do ciclo, pela data em que foram feitos. */
type OrderEvolutionMode = "year" | "cycle";

/** "scope" é o ano ou o ciclo inteiro; as demais navegam por data dentro dele. */
type Scale = "scope" | "month" | "week" | "day";

const scales: Scale[] = ["scope", "month", "week", "day"];
const scaleLabel: Record<Scale, string> = {
  scope: "Visão geral",
  month: "Mês",
  week: "Semana",
  day: "Dia",
};

interface OrderEvent {
  date: Date;
  amount: number;
}

interface ChartWindow {
  from: Date;
  to: Date;
  buckets: { from: Date; to: Date; label: string }[];
}

const currentYear = () => new Date().getFullYear();

/** Mantém o dado anterior na troca de filtro, mas nunca o de outra unidade. */
const keepForSameClient =
  (clientCode: string) =>
    <T,>(previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
      previousQuery?.queryKey[1] === clientCode ? keepPreviousData(previous) : undefined;

/**
 * Meses do escopo. No modo Ano: jan–dez. No modo Ciclo: do primeiro ao último mês
 * com pedido (o ciclo pode começar no ano anterior), com no mínimo 12 meses.
 */
function scopeRange(mode: OrderEvolutionMode, year: number, events: OrderEvent[]) {
  if (mode === "year" || events.length === 0) {
    return { from: new Date(year, 0, 1), months: 12 };
  }
  const from = startOfMonth(events[0].date);
  const last = startOfMonth(events[events.length - 1].date);
  return { from, months: Math.max(12, differenceInCalendarMonths(last, from) + 1) };
}

function buildWindow(
  scale: Scale,
  anchor: Date,
  scope: { from: Date; months: number },
): ChartWindow {
  const build = (
    from: Date,
    count: number,
    next: (date: Date) => Date,
    label: (date: Date) => string,
  ): ChartWindow => {
    const buckets = Array.from({ length: count }, (_, index) => {
      let start = from;
      for (let i = 0; i < index; i++) start = next(start);
      return { from: start, to: next(start), label: label(start) };
    });
    return { from, to: buckets[buckets.length - 1].to, buckets };
  };

  if (scale === "scope") {
    const crossesYear =
      addMonths(scope.from, scope.months - 1).getFullYear() !== scope.from.getFullYear();
    return build(scope.from, scope.months, (date) => addMonths(date, 1), (date) =>
      format(date, crossesYear ? "MMM/yy" : "MMM", { locale: ptBR }),
    );
  }
  if (scale === "month") {
    return build(startOfMonth(anchor), endOfMonth(anchor).getDate(), (date) => addDays(date, 1), (date) =>
      format(date, "d", { locale: ptBR }),
    );
  }
  if (scale === "week") {
    return build(startOfWeek(anchor, { weekStartsOn: 1 }), 7, (date) => addDays(date, 1), (date) =>
      format(date, "EEE d", { locale: ptBR }),
    );
  }
  return build(startOfDay(anchor), 24, (date) => addHours(date, 1), (date) => format(date, "HH'h'"));
}

function chartPoints(events: OrderEvent[], view: ChartWindow) {
  let running = 0;
  const points = view.buckets.map((bucket) => {
    running += events
      .filter((event) => event.date >= bucket.from && event.date < bucket.to)
      .reduce((sum, event) => sum + event.amount, 0);
    return { label: bucket.label, amount: running };
  });
  return { points, total: running };
}

function windowLabel(scale: Scale, mode: OrderEvolutionMode, year: number, view: ChartWindow) {
  if (scale === "scope") {
    if (mode === "year") return String(year);
    const last = addMonths(view.to, -1);
    return `Ciclo ${year} · ${format(view.from, "MMM/yyyy", { locale: ptBR })} a ${format(last, "MMM/yyyy", { locale: ptBR })}`;
  }
  if (scale === "month") return format(view.from, "MMMM 'de' yyyy", { locale: ptBR });
  if (scale === "week") {
    return `${format(view.from, "dd/MM")} a ${format(addDays(view.from, 6), "dd/MM/yyyy")}`;
  }
  return format(view.from, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

function moveAnchor(scale: Exclude<Scale, "scope">, anchor: Date, amount: number) {
  if (scale === "month") return addMonths(anchor, amount);
  if (scale === "week") return addWeeks(anchor, amount);
  return addDays(anchor, amount);
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | null;
  tone?: "default" | "accent" | "muted";
}) {
  return (
    <div className="min-w-0 rounded-xl border border-border/70 bg-white px-4 py-3">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={`mt-1 text-lg font-bold tabular-nums break-words ${tone === "accent" ? "text-primary" : tone === "muted" ? "text-amber-700" : "text-foreground"}`}
      >
        {value === null ? "—" : formatCurrency(value)}
      </dd>
    </div>
  );
}

function YearSelect({
  value,
  options,
  onChange,
  label,
  format: formatOption = String,
}: {
  value: number;
  options: number[];
  onChange: (year: number) => void;
  label: string;
  format?: (year: number) => string;
}) {
  const years = options.includes(value) ? options : [value, ...options];
  return (
    <Select value={String(value)} onValueChange={(next) => onChange(Number(next))}>
      <SelectTrigger aria-label={label} className="w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {years.map((year) => (
          <SelectItem key={year} value={String(year)}>
            {formatOption(year)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface SummaryState {
  data: CustomerPortalSummary | undefined;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
}

function FinancialOverview({
  year,
  onYearChange,
  summary: { data, error, isLoading, isError },
}: {
  year: number;
  onYearChange: (year: number) => void;
  summary: SummaryState;
}) {
  // A API devolve invoicesAmount nulo quando os valores financeiros não estão confirmados.
  const financialValuesKnown = !!data && data.invoicesAmount !== null;

  return (
    <Card className="min-w-0 rounded-2xl border-border/80 bg-white shadow-sm">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-base">Visão financeira</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Pedidos feitos no ano
          </p>
        </div>
        <YearSelect
          value={year}
          options={data?.availableYears ?? []}
          onChange={onYearChange}
          label="Selecionar ano"
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p role="status" className="py-12 text-sm text-muted-foreground">
            Carregando valores...
          </p>
        ) : isError || !data ? (
          <p role="alert" className="py-12 text-sm text-destructive">
            {customerPortalSummaryErrorMessage(error)}
          </p>
        ) : (
          <>
            <dl className="grid gap-2">
              <Metric label="Pedidos realizados" value={data.ordersAmount} />
              <Metric
                label="Devoluções e isenções"
                value={financialValuesKnown ? data.returnsAndExemptionsAmount : null}
              />
              <Metric
                label="Faturas"
                value={financialValuesKnown ? data.invoicesAmount : null}
              />
            </dl>
            <div className="my-4 border-t border-border" />
            <dl className="grid gap-2 sm:grid-cols-2">
              <Metric
                label="Em aberto"
                value={financialValuesKnown ? data.openAmount : null}
                tone="muted"
              />
              <Metric
                label="Pago"
                value={financialValuesKnown ? data.paidAmount : null}
                tone="accent"
              />
            </dl>
            {/* {!data.financialAvailable && (
              <p role="alert" className="mt-4 text-xs text-amber-700">
                O ERP financeiro está indisponível. Os valores financeiros
                serão exibidos quando a consulta voltar.
              </p>
            )} */}
            {/* {!data.hasLinkedInvoices && data.ordersAmount > 0 && (
              <p className="mt-4 text-xs text-amber-700">
                Os pedidos deste ano ainda não têm nota fiscal nem OV
                registradas, por isso os valores financeiros não aparecem.
              </p>
            )} */}
            {/* {financialValuesKnown && data.unmatchedFinancialEntries > 0 && (
              <p className="mt-4 text-xs text-amber-700">
                {data.unmatchedFinancialEntries === 1
                  ? "1 lançamento do ERP não pôde ser vinculado"
                  : `${data.unmatchedFinancialEntries} lançamentos do ERP não puderam ser vinculados`}{" "}
                a pedidos deste ano e ficaram fora dos valores.
              </p>
            )} */}
            {/* {data.hasLinkedInvoices && (
              <p className="mt-4 text-xs text-muted-foreground">
                Período consultado:{" "}
                {format(new Date(data.financialPeriodStart), "dd/MM/yyyy")} a{" "}
                {format(new Date(data.financialPeriodEnd), "dd/MM/yyyy")}
              </p>
            )} */}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function OrderEvolution({
  clientCode,
  initialCycleYear,
  summary: { data, error, isLoading, isError },
}: {
  clientCode: string;
  initialCycleYear: number | null;
  summary: SummaryState;
}) {
  const [mode, setMode] = useState<OrderEvolutionMode>("year");
  const [calendarYear, setCalendarYear] = useState(currentYear);
  const [cycleYear, setCycleYear] = useState(() => initialCycleYear ?? currentYear());
  const [scale, setScale] = useState<Scale>("scope");
  const [anchor, setAnchor] = useState(() => new Date());
  const selectedYear = mode === "year" ? calendarYear : cycleYear;

  useEffect(() => {
    setMode("year");
    setCalendarYear(currentYear());
    setCycleYear(initialCycleYear ?? currentYear());
    setScale("scope");
  }, [clientCode, initialCycleYear]);

  // Os pedidos já vêm no resumo: trocar Ano/Ciclo ou o ano do gráfico não faz nova chamada.
  const events = useMemo<OrderEvent[]>(
    () =>
      (data?.orders ?? [])
        .filter((order) => (mode === "year" ? order.year : order.cycleYear) === selectedYear)
        .map((order) => ({ date: new Date(order.date), amount: order.amount })),
    [data, mode, selectedYear],
  );
  const scope = useMemo(() => scopeRange(mode, selectedYear, events), [mode, selectedYear, events]);
  const view = useMemo(() => buildWindow(scale, anchor, scope), [scale, anchor, scope]);
  const chart = useMemo(() => chartPoints(events, view), [events, view]);
  const label = windowLabel(scale, mode, selectedYear, view);

  const selectYear = (year: number) => {
    if (mode === "year") setCalendarYear(year);
    else setCycleYear(year);
    setScale("scope");
  };

  const chooseMode = (next: OrderEvolutionMode) => {
    setMode(next);
    setScale("scope");
  };

  const chooseScale = (next: Scale) => {
    // Ao aproximar, começa pelo pedido mais recente da janela que estava na tela.
    const latestInView = events
      .filter((event) => event.date >= view.from && event.date < view.to)
      .at(-1)?.date;
    setAnchor(latestInView ?? view.from);
    setScale(next);
  };

  const move = (amount: number) => {
    if (scale === "scope") selectYear(selectedYear + amount);
    else setAnchor((date) => moveAnchor(scale, date, amount));
  };

  return (
    <Card className="min-w-0 rounded-2xl border-border/80 bg-white shadow-sm">
      <CardHeader className="space-y-3 pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-primary" /> Evolução dos
              pedidos
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Pedidos feitos no período
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Filtro: por ano civil ou por ciclo; o seletor ao lado escolhe qual. */}
            <div
              className="inline-flex rounded-lg border border-primary/30 bg-white p-0.5"
              role="group"
              aria-label="Filtrar pedidos por"
            >
              {(["year", "cycle"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={mode === item}
                  onClick={() => chooseMode(item)}
                  className={`h-8 min-w-14 rounded-md px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${mode === item ? "bg-primary font-semibold text-primary-foreground shadow-sm" : "font-medium text-muted-foreground hover:bg-primary/10 hover:text-primary"}`}
                >
                  {item === "year" ? "Ano" : "Ciclo"}
                </button>
              ))}
            </div>
            <YearSelect
              value={selectedYear}
              options={(mode === "year" ? data?.availableYears : data?.availableCycleYears) ?? []}
              onChange={selectYear}
              label={mode === "year" ? "Selecionar ano" : "Selecionar ciclo"}
              format={mode === "year" ? String : (year) => `Ciclo ${year}`}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Escala do gráfico">
          {scales.map((item) => (
            <Button
              key={item}
              size="sm"
              variant={scale === item ? "default" : "ghost"}
              aria-pressed={scale === item}
              onClick={() => chooseScale(item)}
              className="h-8 px-3 text-xs"
            >
              {scaleLabel[item]}
            </Button>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-2 py-1">
          <Button size="icon" variant="ghost" aria-label="Período anterior" onClick={() => move(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-center text-xs font-semibold">{label}</span>
          <Button size="icon" variant="ghost" aria-label="Próximo período" onClick={() => move(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p
            role="status"
            className="flex h-56 items-center justify-center text-sm text-muted-foreground"
          >
            Carregando gráfico...
          </p>
        ) : isError || !data ? (
          <p
            role="alert"
            className="flex h-56 items-center justify-center text-sm text-destructive"
          >
            {customerPortalSummaryErrorMessage(error)}
          </p>
        ) : (
          <>
            <div className="mb-3 text-xl font-bold tabular-nums">
              {formatCurrency(chart.total)}
            </div>
            <div
              className="h-64 w-full xl:h-[280px]"
              role="img"
              aria-label={`Evolução acumulada dos pedidos em ${label}`}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chart.points}
                  margin={{ top: 8, right: 8, bottom: 0, left: -14 }}
                >
                  <defs>
                    <linearGradient id="ordersArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                    minTickGap={14}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(value)
                    }
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip formatter={(value: number) => [formatCurrency(value), "Acumulado"]} />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    fill="url(#ordersArea)"
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {chart.total === 0 && (
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Nenhum pedido neste período.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function CustomerPortalOverview({
  clientCode,
  initialCycleYear,
}: {
  clientCode: string;
  initialCycleYear: number | null;
}) {
  const [year, setYear] = useState(currentYear);

  useEffect(() => setYear(currentYear()), [clientCode]);

  const summary = useQuery({
    queryKey: ["customer-portal-summary", clientCode, year],
    queryFn: async () => (await customerPortalApi.getSummary(clientCode, year)).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
    placeholderData: keepForSameClient(clientCode),
  });

  return (
    <div className="grid min-w-0 gap-5 lg:grid-cols-2 xl:col-span-2">
      <FinancialOverview year={year} onYearChange={setYear} summary={summary} />
      <OrderEvolution clientCode={clientCode} initialCycleYear={initialCycleYear} summary={summary} />
    </div>
  );
}
